import { useEffect, useState } from "react";
import { api, CATEGORY_COLORS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["blue", "emerald", "rose", "amber", "violet", "slate"];

export default function Categories() {
    const [categories, setCategories] = useState([]);
    const [name, setName] = useState("");
    const [color, setColor] = useState("blue");

    const load = async () => {
        const c = await api.get("/categories");
        setCategories(c.data);
    };

    useEffect(() => {
        load();
    }, []);

    const add = async () => {
        if (!name.trim()) return toast.error("Nom requis");
        try {
            await api.post("/categories", { name, color });
            setName("");
            toast.success("Catégorie ajoutée");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Supprimer la catégorie "${c.name}" ?`)) return;
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success("Catégorie supprimée");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    return (
        <>
            <PageHeader
                title="Catégories"
                subtitle="Organisez votre inventaire par type de produit"
            />
            <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                <Card className="border-slate-200 shadow-sm p-6 lg:col-span-1 h-fit">
                    <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">
                        Nouvelle catégorie
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Nom
                            </Label>
                            <Input
                                data-testid="category-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex : Véhicules"
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Couleur
                            </Label>
                            <Select value={color} onValueChange={setColor}>
                                <SelectTrigger
                                    data-testid="category-color-select"
                                    className="mt-2 h-11"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {COLORS.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`w-3 h-3 rounded-full ${
                                                        CATEGORY_COLORS[c].split(" ")[0]
                                                    }`}
                                                />
                                                {c}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            onClick={add}
                            data-testid="category-add-btn"
                            className="w-full bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                        >
                            Ajouter
                        </Button>
                    </div>
                </Card>

                <Card className="border-slate-200 shadow-sm lg:col-span-2 overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-200">
                        <h2 className="font-display text-lg font-semibold text-slate-900">
                            Catégories existantes
                        </h2>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {categories.length === 0 && (
                            <div className="px-6 py-10 text-center text-slate-500 text-sm">
                                Aucune catégorie
                            </div>
                        )}
                        {categories.map((c) => (
                            <div
                                key={c.id}
                                data-testid={`category-row-${c.id}`}
                                className="px-6 py-4 flex items-center justify-between hover:bg-slate-50"
                            >
                                <div className="flex items-center gap-3">
                                    <Badge className={`${CATEGORY_COLORS[c.color] || CATEGORY_COLORS.slate} border`}>
                                        {c.name}
                                    </Badge>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`category-delete-btn-${c.id}`}
                                    onClick={() => remove(c)}
                                    className="text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </>
    );
}
